import { Suspense, useContext, useEffect, useState } from "react";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  IRepossession,
  REPOSSESSION_STATUS,
} from "../../../types/repossession.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import { getRepossessions } from "../../../common/services/repossession.api";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { formatDateAndTime } from "../../../common/helpers/date.helpers";
import { useNavigate } from "react-router-dom";
import ModalSuspendRepossession from "./modals/ModalSuspendRepossession";
import {
  savePublickLink,
  toggleVehicleBlockStatus,
} from "../../../common/services/vehicles.api";
import { FaBuilding, FaCircle, FaInfoCircle, FaUser } from "react-icons/fa";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";
import ModalCollector from "./modals/ModalCollector";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import ModalRemoveCollector from "./modals/ModalRemoveCollector";
import ModalViewNotes from "./modals/ModalViewNotes";
import ModalAddNote from "./modals/ModalAddNote";
import { useCompanyStore } from "@/common/states/store/Company";
import { maskPhone } from "@/common/helpers/mask.helpers";

const EMPTY_ACTION = { eventName: "", data: undefined };

/** Auto-refresh while this screen is open (30 min). Skips when the browser tab is hidden. */
const REPOSSESSION_AUTO_REFRESH_MS = 30 * 60 * 1000;

const EMPTY_SUMMARY = {
  upTo3Days: 0,
  upTo15Days: 0,
  above15Days: 0,
};

const STATUS_LABELS = {
  [REPOSSESSION_STATUS.PENDING]: "Pendente",
  [REPOSSESSION_STATUS.COMPLETED]: "Concluído",
  [REPOSSESSION_STATUS.SUSPENDED]: "Suspenso",
  [REPOSSESSION_STATUS.CANCELED]: "Cancelado",
};

function getStatusLabels(status: REPOSSESSION_STATUS) {
  return STATUS_LABELS[status] || status;
}

const headers = {
  city: "Cidade",
  renter: "Cliente",
  whatsapp: "Telefone",
  vehicle: "Veículo",
  location: "Localização",
  lastComunication: "Última comunicação",
  startedAt: "Início do Recolhimento",
  completedAt: "Fim do Recolhimento",
  status: "Status",
  reason: "Motivo",
  notes: "Observações",
};

type RepossessionTableItem = IRepossession & {
  status?: REPOSSESSION_STATUS | string;
  details?: {
    daysOverdue?: number | string;
  };
};

type OverdueFilter = "upTo3Days" | "from3To15Days" | "above15Days";

function getDaysOverdue(repossession: RepossessionTableItem) {
  return Number(repossession?.details?.daysOverdue ?? 0);
}

function getRepossessionSummary(items: RepossessionTableItem[]) {
  return items.reduce(
    (summary, repossession) => {
      const daysOverdue = getDaysOverdue(repossession);

      if (daysOverdue <= 3) {
        summary.upTo3Days += 1;
      } else if (daysOverdue <= 15) {
        summary.upTo15Days += 1;
      } else {
        summary.above15Days += 1;
      }

      return summary;
    },
    { ...EMPTY_SUMMARY }
  );
}

function getRepossessionRowClass(data: Record<string, unknown>) {
  const daysOverdue = getDaysOverdue(data as RepossessionTableItem);

  if (daysOverdue <= 3) {
    return "text-[rgb(97,97,97)] [&_a]:!text-[rgb(97,97,97)] [&_button]:!text-[rgb(97,97,97)] [&_span]:!text-[rgb(97,97,97)]";
  }

  if (daysOverdue <= 15) {
    return "text-[#d08e14] [&_a]:!text-[#d08e14] [&_button]:!text-[#d08e14] [&_span]:!text-[#d08e14]";
  }

  return "text-[rgb(228,65,49)] [&_a]:!text-[rgb(228,65,49)] [&_button]:!text-[rgb(228,65,49)] [&_span]:!text-[rgb(228,65,49)]";
}

function matchesOverdueFilter(
  repossession: RepossessionTableItem,
  overdueFilter: OverdueFilter
) {
  const daysOverdue = getDaysOverdue(repossession);

  if (overdueFilter === "upTo3Days") {
    return daysOverdue <= 3;
  }

  if (overdueFilter === "from3To15Days") {
    return daysOverdue > 3 && daysOverdue <= 15;
  }

  return daysOverdue > 15;
}

function TableRepossession() {
  useContext(AuthContext);

  const navigate = useNavigate();
  const company = useCompanyStore();

  const { notifySuccess, notifyError } = useToastContext();
  const [data, setData] = useState({
    itemsPerPage: 800,
    totalItems: 0,
  });
  const [repossessions, setRepossessions] = useState<RepossessionTableItem[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefreshToken, setAutoRefreshToken] = useState(0);
  const [page, setPage] = useState<number>(1);

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [selectedOverdueFilter, setSelectedOverdueFilter] =
    useState<OverdueFilter | null>(null);

  async function handlePublicLinkVelotrack(vehicle) {
    try {
      setIsLoading(true);
      const result = await savePublickLink(vehicle);
      if (!result.ok) {
        notifyError("Veiculo não encontrado na Velotrack");
        return;
      }

      if (result.data?.link) {
        window.open(result.data.link, "_blank");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    async function loadRepossessions() {
      setIsLoading(true);
      setRepossessions([]);
      const filtersCopy = { ...filters };

      if (filtersCopy.status.length === 0) {
        filtersCopy.status = [REPOSSESSION_STATUS.PENDING];
      }
      const result = await getRepossessions({
        ...filtersCopy,
        page: page,
        itemsPerPage: data.itemsPerPage, // Isso foi necessário para diminuir o tamanho do payload por conta do Api Gatway que é de 6MB
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.pageTotalItens,
      });

      if (result && !result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      setRepossessions(result.data);
      setIsLoading(false);
    }

    loadRepossessions();
  }, [filters, company, data.itemsPerPage, notifyError, page, autoRefreshToken]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setAutoRefreshToken((t) => t + 1);
    }, REPOSSESSION_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  const changeVehicleBlockStatus = async (
    eventName: string,
    vehicleId: string
  ) => {
    const result = await toggleVehicleBlockStatus(vehicleId, {
      velotrackSettings: {
        blocked: eventName === "block",
        blockedDate: new Date(),
      },
    });
    if (result.ok) {
      notifySuccess(
        `Veículo ${eventName === "block" ? "bloqueado" : "desbloqueado"}!`
      );
    } else {
      notifyError("Não foi possível alterar o status de bloqueio!");
    }
  };

  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => setAction(EMPTY_ACTION);
  const repossessionSummary = getRepossessionSummary(repossessions);
  const shouldHighlightAbove15Days = repossessionSummary.above15Days > 0;
  const filteredRepossessions = selectedOverdueFilter
    ? repossessions.filter((repossession) =>
      matchesOverdueFilter(repossession, selectedOverdueFilter)
    )
    : repossessions;

  const handleOverdueCardClick = (overdueFilter: OverdueFilter) => {
    setSelectedOverdueFilter((currentFilter) =>
      currentFilter === overdueFilter ? null : overdueFilter
    );
    setPage(1);
  };

  const actions = [
    {
      label: "Vincular Recolhedor",
      visible: (data) => {
        if (data?.vehicle?.inBase) return false;
        if (data?.collector?.id) return false;
        return data?.status === "PENDING";
      },
      action: (data) => {
        return setAction({ eventName: "collector", data: { plate: data.vehicle.licensePlate, repossessionId: data._id } })
      },
      permission: "collector:repossession",
    },
    {
      label: "Alterar Recolhedor",
      visible: (data) => {
        if (data?.vehicle?.repossession?.collector?.id) return true;
        if (data?.vehicle?.inBase) return false;
        if (data?.status == "PENDING") return true;
        return false;
      },
      action: (data) => {
        setAction({ eventName: "collector", data: { plate: data.vehicle.licensePlate, repossessionId: data._id } })
      },
      permission: "collector:repossession",
    },
    {
      label: "Suspender",
      visible: (data) => {
        if (data?.status !== "SUSPENDED") return true;
        return false;
      },
      action: (data) =>
        setAction({ eventName: "edit", data: data.vehicle?.licensePlate }),
      permission: "suspend:repossession",
    },
    {
      label: "Remover Recolhedor",
      visible: (data) => {
        return !!data?.collector?.id; // só aparece se já existir collector
      },
      action: (data) =>
        setAction({
          eventName: "removeCollector",
          data: {
            repossessionId: data._id, // <-- aqui é o _id da repossession
            collectorId: data.collector.id, // <-- aqui é o id do collector
          },
        }),
      permission: "collector:repossession",
    },
    {
      label: "Bloquear Veículo",
      visible: (data) => {
        if (!data?.vehicle?.velotrackSettings?.blocked) return true;
        return false;
      },
      action: (data) => changeVehicleBlockStatus("block", data.vehicle._id),
      permission: "block:vehicle-repossession",
    },
    {
      label: "Desbloquear Veículo",
      visible: (data) => {
        if (data?.vehicle?.velotrackSettings?.blocked) return true;
        return false;
      },
      action: (data) => changeVehicleBlockStatus("unblock", data.vehicle?._id),
      permission: "unblock:vehicle-repossession",
    },
    {
      label: "Adicionar Observação",
      visible: () => true,
      action: (data) => setAction({ eventName: "add-note", data: data }),
    },
  ];

  const formatStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _data: Record<string, unknown>
  ): React.ReactNode => {
    return content;
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Recolhimento"
          placeholder="Digite o cliente ou a placa"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          onNewItemClick={() =>
            setAction({ eventName: "edit", data: undefined })
          }
          entity="repossession"
          tableData={repossessions}
        />

        {!isLoading && (
          <div className="grid grid-cols-2 gap-3 mt-1 mb-3 lg:grid-cols-3">
            <button
              type="button"
              onClick={() => handleOverdueCardClick("upTo3Days")}
              className={`bg-[rgb(97,97,97)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition text-left ${selectedOverdueFilter === "upTo3Days"
                  ? "ring-2 ring-offset-2 ring-[rgb(97,97,97)]"
                  : ""
                }`}
            >
              <h2 className="text-base text-white uppercase w-1/2">
                Até 3 dias
              </h2>
              <p className="text-lg text-white w-1/2 text-right">
                {repossessionSummary.upTo3Days}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleOverdueCardClick("from3To15Days")}
              className={`bg-[#ffa600] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition text-left ${selectedOverdueFilter === "from3To15Days"
                  ? "ring-2 ring-offset-2 ring-[#ffa600]"
                  : ""
                }`}
            >
              <h2 className="text-base text-white uppercase w-1/2">
                De 3 à 15 dias
              </h2>
              <p className="text-lg text-white w-1/2 text-right">
                {repossessionSummary.upTo15Days}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleOverdueCardClick("above15Days")}
              className={`bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition text-left ${shouldHighlightAbove15Days
                  ? "animate-pulse font-semibold animate-pulse-color"
                  : ""
                } ${selectedOverdueFilter === "above15Days"
                  ? "ring-2 ring-offset-2 ring-[rgb(228,65,49)]"
                  : ""
                }`}
            >
              <div className="w-full ">
                <h2 className="text-base text-white uppercase w-1/2">
                  Mais de 15 dias
                </h2>

                <p className="text-sm text-white w-1/2 ">
                  Registrar apropiação indébita.
                </p>
              </div>

              <p className="text-lg text-white w-1/2 text-right">
                {repossessionSummary.above15Days}
              </p>
            </button>

          </div>
        )}

        <GenericTable
          headers={headers}
          data={filteredRepossessions}
          actions={actions}
          rowClassName={getRepossessionRowClass}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              city: (city, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {city}
                  </span>,
                  data
                );
              },
              renter: (renter, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    <a
                      style={{ fontSize: "15px" }}
                      href={`/clientes/${renter.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/clientes/${renter.id}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {renter.name}
                    </a>
                  </span>,
                  data
                );
              },
              whatsapp: (_, data) => {
                return formatStyle(
                  <a
                    href={`https://wa.me/+55${data.renter.whatsapp.replace(
                      /\D/g,
                      ""
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {maskPhone(data.renter.whatsapp)}
                  </a>,
                  data
                );
              },
              vehicle: (vehicle, data) => {
                return formatStyle(
                  <span className="flex items-center gap-1">
                    {data.collector?.id && (
                      <FaUser title={data.collector.name} />
                    )}
                    {data.vehicleInBase && (
                      <FaBuilding title="Veículo na Base" />
                    )}
                    <FaCircle
                      className={
                        vehicle.velotrackSettings.blocked
                          ? "text-redPrimary"
                          : "text-greenPrimary"
                      }
                      title={
                        vehicle.velotrackSettings.blocked
                          ? "Veículo bloqueado"
                          : "Veículo desbloqueado"
                      }
                    />
                    <a
                      style={{ fontSize: "15px" }}
                      href={`/veiculos/${vehicle.licensePlate}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/veiculos/${vehicle.licensePlate}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {vehicle.licensePlate}
                    </a>
                  </span>,
                  data
                );
              },
              lastComunication: (_, data) => {
                const lastCommunicationDate =
                  data.vehicle?.velotrackSettings?.lastComunication;

                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {formatDateAndTime(lastCommunicationDate)}
                  </span>,
                  data
                );
              },
              location: (vehicle, data) => {
                return formatStyle(
                  vehicle.latitude && vehicle.longitude ? (
                    <a
                      href={`https://www.google.com/maps?q=${vehicle.latitude},${vehicle.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline cursor-pointer"
                    >
                      Link
                    </a>
                  ) : (
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePublicLinkVelotrack(vehicle);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline cursor-pointer"
                    >
                      Link
                    </a>
                  ),
                  data
                );
              },
              startedAt: (startedAt, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {formatDateAndTime(startedAt)}
                  </span>,
                  data
                );
              },
              completedAt: (completedAt, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {formatDateAndTime(completedAt)}
                  </span>,
                  data
                );
              },
              status: (status, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {getStatusLabels(status)}
                  </span>,
                  data
                );
              },
              reason: (reason, data) => {
                return formatStyle(
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {reason === "NON-PAYMENT" ? "Inadimplência" : "Outro"}
                  </span>,
                  data
                );
              },
              notes: (notes, data) => {
                const hasNotes = notes && notes.length > 0;

                return formatStyle(
                  <div className="inline-flex items-center gap-[4px]">
                    {hasNotes ? (
                      <button
                        title="Visualizar observações"
                        className="inline-flex items-center gap-[4px]  hover:text-red-800 p-0 m-0 leading-none font-semibold text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAction({ eventName: "view-notes", data });
                        }}
                      >
                        <span>{notes}</span>
                        <FaInfoCircle size="20px" />
                      </button>
                    ) : (
                      <span className="font-semibold text-sm text-red">
                        {notes}
                      </span>
                    )}
                  </div>,
                  data
                );
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="repossession"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={
            selectedOverdueFilter ? filteredRepossessions.length : data.totalItems || 0
          }
          itemsPerPage={+(filters.itemsPerPage ?? 80)}
          goToPage={setPage}
        />

        {isAction("edit") && (
          <ModalSuspendRepossession
            vehicle={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("collector") && (
          <ModalCollector
            data={action.data as { plate: string; repossessionId: string }}
            onClose={cleanActionData}
          />
        )}
        {isAction("view-notes") && (
          <ModalViewNotes
            repossession={action.data as IRepossession}
            onClose={cleanActionData}
          />
        )}
        {isAction("removeCollector") && (
          <ModalRemoveCollector
            open={true}
            onClose={cleanActionData}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            repossessionId={(action.data as any).repossessionId}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            collectorId={(action.data as any).collectorId}
            onSuccess={() => {
              notifySuccess("Recolhedor removido com sucesso!");
              cleanActionData();
            }}
          />
        )}
        {isAction("add-note") && (
          <ModalAddNote
            repossession={action.data as IRepossession}
            onClose={cleanActionData}
            onSuccess={() => {
              notifySuccess("Observação adicionada com sucesso!");
              cleanActionData();
            }}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableRepossession;
